import React, { useState, useEffect, useCallback } from 'react';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

const LineItemsTable = ({ 
  items: lineItems = [], 
  onChange, 
  productItems: itemOptions = [], 
  taxSettings = { taxType: 'tax_exclusive', taxLevel: 'line_item_level' },
  currency = 'USD',
  readOnly = false,
  itemsLoading = false
}) => {
  const [items, setItems] = useState([]);
  const [initialized, setInitialized] = useState(false);

  // CRITICAL FIX: Enhanced synchronization with parent component lineItems
  useEffect(() => {
    console.log('LineItemsTable: Props changed', {
      lineItemsLength: lineItems?.length,
      lineItems: lineItems,
      initialized,
      readOnly
    });

    // ALWAYS sync with incoming lineItems when they exist (edit mode priority)
    if (lineItems && lineItems?.length > 0) {
      console.log('LineItemsTable: Loading existing line items for edit mode', lineItems);
      setItems([...lineItems]); // Create new array to trigger re-render
      setInitialized(true);
    } 
    // Handle case where lineItems becomes empty (form reset)
    else if (lineItems && lineItems?.length === 0 && initialized) {
      console.log('LineItemsTable: Resetting line items (form reset)');
      setItems([]);
    }
    // Initialize empty line item for new invoices only when not in read-only mode
    else if (!initialized && !readOnly && (!lineItems || lineItems?.length === 0)) {
      console.log('LineItemsTable: Initializing empty line item for new invoice');
      const newItem = {
        id: Date.now(),
        line_number: 1,
        item_id: '',
        item_description: '',
        quantity: 1,
        unit_price: 0,
        tax_rate: 0,
        tax_amount: 0,
        line_total: 0,
        line_total_with_tax: 0,
        notes: '',
        weight_per_unit: 1
      };
      setItems([newItem]);
      setInitialized(true);
      onChange?.([newItem]);
    }
    // Mark as initialized even if no items (read-only or waiting for data)
    else if (!initialized) {
      setInitialized(true);
    }
  }, [lineItems, initialized, readOnly, onChange]);

  // CRITICAL FIX: Force re-sync when lineItems prop changes externally
  useEffect(() => {
    // This ensures that if the parent updates lineItems after initial load, we sync immediately
    if (initialized && lineItems && Array.isArray(lineItems)) {
      // Only update if the items are actually different (avoid infinite loops)
      const currentItemIds = items?.map(item => item?.id)?.sort();
      const newItemIds = lineItems?.map(item => item?.id)?.sort();
      
      if (JSON.stringify(currentItemIds) !== JSON.stringify(newItemIds) || 
          lineItems?.length !== items?.length) {
        console.log('LineItemsTable: Force re-sync with updated line items from parent', {
          currentItemsCount: items?.length,
          newLineItemsCount: lineItems?.length,
          newLineItems: lineItems
        });
        setItems([...lineItems]);
      }
    }
  }, [lineItems, items, initialized]);

  // Memoized addLineItem function to prevent unnecessary re-renders
  const addLineItem = useCallback(() => {
    const newItem = {
      id: Date.now(), // Temporary ID for new items
      line_number: items?.length + 1,
      item_id: '',
      item_description: '',
      quantity: 1,
      unit_price: 0,
      tax_rate: 0,
      tax_amount: 0,
      line_total: 0,
      line_total_with_tax: 0,
      notes: '',
      weight_per_unit: 1
    };
    
    const updatedItems = [...items, newItem];
    setItems(updatedItems);
    onChange?.(updatedItems);
  }, [items, onChange]);

  const removeLineItem = useCallback((index) => {
    const updatedItems = items?.filter((_, i) => i !== index);
    // Renumber line items
    const renumberedItems = updatedItems?.map((item, i) => ({
      ...item,
      line_number: i + 1
    }));
    setItems(renumberedItems);
    onChange?.(renumberedItems);
  }, [items, onChange]);

  const updateLineItem = useCallback((index, field, value) => {
    const updatedItems = [...items];
    updatedItems[index] = {
      ...updatedItems?.[index],
      [field]: value
    };

    // Auto-populate item details when item is selected
    if (field === 'item_id' && value) {
      const selectedItem = itemOptions?.find(item => item?.value === value);
      if (selectedItem?.item) {
        updatedItems[index] = {
          ...updatedItems?.[index],
          item_description: selectedItem?.item?.item_name,
          unit_price: parseFloat(selectedItem?.item?.selling_price || 0),
          tax_rate: parseFloat(selectedItem?.item?.tax_rate || 0),
          weight_per_unit: parseFloat(selectedItem?.item?.weight_per_unit || 1)
        };
      }
    }

    // Calculate line totals
    const quantity = parseFloat(updatedItems?.[index]?.quantity || 0);
    const unitPrice = parseFloat(updatedItems?.[index]?.unit_price || 0);
    const taxRate = parseFloat(updatedItems?.[index]?.tax_rate || 0);

    const lineTotal = quantity * unitPrice;
    const taxAmount = taxSettings?.taxType === 'tax_inclusive' 
      ? lineTotal - (lineTotal / (1 + taxRate / 100))
      : lineTotal * (taxRate / 100);
    const lineTotalWithTax = taxSettings?.taxType === 'tax_inclusive' 
      ? lineTotal 
      : lineTotal + taxAmount;

    updatedItems[index] = {
      ...updatedItems?.[index],
      line_total: lineTotal,
      tax_amount: taxAmount,
      line_total_with_tax: lineTotalWithTax
    };

    setItems(updatedItems);
    onChange?.(updatedItems);
  }, [items, itemOptions, taxSettings, onChange]);

  const formatCurrency = useCallback((amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency || 'USD',
      minimumFractionDigits: 2
    })?.format(amount || 0);
  }, [currency]);

  const calculateTotals = useCallback(() => {
    const subtotal = items?.reduce((sum, item) => sum + (parseFloat(item?.line_total) || 0), 0);
    const totalTax = items?.reduce((sum, item) => sum + (parseFloat(item?.tax_amount) || 0), 0);
    const total = items?.reduce((sum, item) => sum + (parseFloat(item?.line_total_with_tax) || 0), 0);

    return { subtotal, totalTax, total };
  }, [items]);

  const totals = calculateTotals();

  // Handle button click with proper event handling
  const handleAddItemClick = useCallback((e) => {
    e?.preventDefault();
    e?.stopPropagation();
    addLineItem();
  }, [addLineItem]);

  // CRITICAL FIX: Show empty state only when truly no items and initialized
  // This prevents premature empty state display during loading
  const shouldShowEmptyState = initialized && items?.length === 0 && !readOnly;

  // Debug logging
  useEffect(() => {
    console.log('LineItemsTable Debug:', {
      propsLineItems: lineItems?.length || 0,
      stateItems: items?.length || 0,
      initialized,
      readOnly,
      shouldShowEmptyState
    });
  }, [lineItems, items, initialized, readOnly, shouldShowEmptyState]);

  if (readOnly) {
    return (
      <div className="space-y-6">
        {/* Enhanced Header */}
        <div className="flex items-center justify-between bg-gradient-to-r from-blue-50 to-indigo-50 p-4 rounded-lg border border-blue-200">
          <div className="flex items-center">
            <div className="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center mr-3">
              <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
              </svg>
            </div>
            <div>
              <h3 className="text-xl font-bold text-gray-900">Invoice Line Items</h3>
              <p className="text-sm text-gray-600">Detailed breakdown of products and services</p>
            </div>
          </div>
          <div className="flex items-center space-x-3">
            <span className="inline-flex items-center px-3 py-1 text-sm font-medium bg-blue-100 text-blue-800 rounded-full">
              <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              {items?.length} item{items?.length !== 1 ? 's' : ''}
            </span>
          </div>
        </div>
        {/* Enhanced Table */}
        <div className="bg-white shadow-lg rounded-lg overflow-hidden border border-gray-200">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider w-16 bg-gray-100">
                    #
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider bg-gray-100">
                    Product / Service
                  </th>
                  <th className="px-6 py-4 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider w-24 bg-gray-100">
                    Qty
                  </th>
                  <th className="px-6 py-4 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider w-32 bg-gray-100">
                    Unit Price
                  </th>
                  <th className="px-6 py-4 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider w-28 bg-gray-100">
                    Tax Rate
                  </th>
                  <th className="px-6 py-4 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider w-32 bg-gray-100">
                    Line Total
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {items?.map((item, index) => (
                  <tr key={item?.id || index} className="hover:bg-gray-50 transition-colors duration-150">
                    <td className="px-6 py-4 text-sm font-bold text-gray-900 bg-gray-50">
                      <div className="w-8 h-8 bg-blue-100 text-blue-800 rounded-full flex items-center justify-center text-xs font-bold">
                        {item?.line_number}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm font-semibold text-gray-900">{item?.item_description}</div>
                      {item?.notes && (
                        <div className="text-xs text-gray-500 mt-1 italic bg-gray-50 px-2 py-1 rounded">
                          Note: {item?.notes}
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 text-center">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                        {item?.quantity}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-right text-sm font-medium text-gray-900">
                      {formatCurrency(item?.unit_price)}
                    </td>
                    <td className="px-6 py-4 text-center">
                      <div className="text-center">
                        <span className="text-sm font-medium text-gray-900">{item?.tax_rate}%</span>
                        <div className="text-xs text-gray-500">({formatCurrency(item?.tax_amount)})</div>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <div className="text-lg font-bold text-green-700 bg-green-50 px-3 py-1 rounded-lg border border-green-200">
                        {formatCurrency(item?.line_total_with_tax)}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        {/* Enhanced Totals Section */}
        <div className="bg-gradient-to-r from-gray-50 to-gray-100 rounded-lg p-6 border border-gray-200 shadow-sm">
          <div className="flex justify-end">
            <div className="w-96 bg-white rounded-lg p-6 shadow-md border border-gray-200">
              <h4 className="text-lg font-bold text-gray-900 mb-6 flex items-center">
                <svg className="w-5 h-5 mr-2 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                </svg>
                Invoice Summary
              </h4>
              <div className="space-y-4">
                <div className="flex justify-between py-2 border-b border-gray-100">
                  <span className="text-sm font-medium text-gray-600">Subtotal:</span>
                  <span className="text-sm font-bold text-gray-900">{formatCurrency(totals?.subtotal)}</span>
                </div>
                <div className="flex justify-between py-2 border-b border-gray-100">
                  <span className="text-sm font-medium text-gray-600">Total Tax:</span>
                  <span className="text-sm font-bold text-gray-900">{formatCurrency(totals?.totalTax)}</span>
                </div>
                <div className="flex justify-between py-3 border-t-2 border-gray-300">
                  <span className="text-lg font-bold text-gray-900">Grand Total:</span>
                  <span className="text-xl font-bold text-green-700">{formatCurrency(totals?.total)}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Enhanced Header with Action */}
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-6 border border-blue-200 shadow-sm">
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <div className="w-12 h-12 bg-blue-600 rounded-lg flex items-center justify-center mr-4">
              <svg className="w-7 h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
              </svg>
            </div>
            <div>
              <h3 className="text-2xl font-bold text-gray-900">Invoice Line Items</h3>
              <p className="text-sm text-gray-600 mt-1">Add products and services to this invoice</p>
            </div>
          </div>
          <Button
            type="button"
            variant="primary"
            size="lg"
            onClick={handleAddItemClick}
            disabled={itemsLoading}
            className="px-6 py-3 shadow-lg hover:shadow-xl transition-shadow"
          >
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
            </svg>
            Add New Item
          </Button>
        </div>
      </div>
      
      {/* Show data table when there are items */}
      {items?.length > 0 && (
        <>
          {/* Enhanced Table Container */}
          <div className="bg-white shadow-xl rounded-lg overflow-hidden border border-gray-200">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gradient-to-r from-gray-100 to-gray-200">
                  <tr>
                    <th className="px-4 py-4 text-center text-xs font-bold text-gray-700 uppercase tracking-wider w-16">
                      #
                    </th>
                    <th className="px-4 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider min-w-[320px]">
                      Product / Service Details
                    </th>
                    <th className="px-4 py-4 text-center text-xs font-bold text-gray-700 uppercase tracking-wider w-24">
                      Quantity
                    </th>
                    <th className="px-4 py-4 text-center text-xs font-bold text-gray-700 uppercase tracking-wider w-32">
                      Unit Price
                    </th>
                    <th className="px-4 py-4 text-center text-xs font-bold text-gray-700 uppercase tracking-wider w-24">
                      Tax %
                    </th>
                    <th className="px-4 py-4 text-right text-xs font-bold text-gray-700 uppercase tracking-wider w-36">
                      Line Total
                    </th>
                    <th className="px-4 py-4 text-center text-xs font-bold text-gray-700 uppercase tracking-wider w-20">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {items?.map((item, index) => (
                    <tr key={item?.id || index} className="hover:bg-blue-50 transition-colors duration-200 group">
                      <td className="px-4 py-6 text-center bg-gray-50 group-hover:bg-blue-100">
                        <div className="w-10 h-10 bg-gradient-to-r from-blue-500 to-indigo-600 text-white rounded-full flex items-center justify-center font-bold text-sm shadow-md">
                          {item?.line_number}
                        </div>
                      </td>
                      <td className="px-4 py-6 space-y-3">
                        <div className="bg-gray-50 p-3 rounded-lg border border-gray-200">
                          <label className="block text-xs font-semibold text-gray-700 mb-2">Select Item</label>
                          <Select
                            value={item?.item_id || ''}
                            onChange={(value) => updateLineItem(index, 'item_id', value)}
                            options={[
                              { value: '', label: 'Choose from inventory...' },
                              ...itemOptions
                            ]}
                            placeholder="Select item from inventory"
                            size="md"
                            className="w-full"
                            disabled={itemsLoading}
                          />
                          {itemsLoading && (
                            <div className="text-xs text-blue-600 mt-1 flex items-center">
                              <svg className="animate-spin -ml-1 mr-2 h-3 w-3 text-blue-600" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                              </svg>
                              Loading items...
                            </div>
                          )}
                          {!itemsLoading && itemOptions?.length === 0 && (
                            <div className="text-xs text-amber-600 mt-2 p-2 bg-amber-50 rounded flex items-center">
                              <svg className="w-4 h-4 inline mr-1" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                              </svg>
                              No items available. Please ensure you're logged in and have items in your inventory.
                            </div>
                          )}
                          {!itemsLoading && itemOptions?.length === 1 && itemOptions?.[0]?.disabled && (
                            <div className="text-xs text-red-600 mt-2 p-2 bg-red-50 rounded flex items-center">
                              <svg className="w-4 h-4 inline mr-1" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                              </svg>
                              {itemOptions?.[0]?.label}
                            </div>
                          )}
                        </div>
                        <div className="bg-gray-50 p-3 rounded-lg border border-gray-200">
                          <label className="block text-xs font-semibold text-gray-700 mb-2">Description</label>
                          <Input
                            value={item?.item_description || ''}
                            onChange={(e) => updateLineItem(index, 'item_description', e?.target?.value)}
                            placeholder="Enter item description"
                            size="md"
                            className="w-full font-medium"
                          />
                        </div>
                        <div className="bg-yellow-50 p-3 rounded-lg border border-yellow-200">
                          <label className="block text-xs font-semibold text-yellow-800 mb-2">Additional Notes (Optional)</label>
                          <Input
                            value={item?.notes || ''}
                            onChange={(e) => updateLineItem(index, 'notes', e?.target?.value)}
                            placeholder="Add special instructions or notes"
                            size="sm"
                            className="w-full text-sm text-yellow-900 bg-yellow-50"
                          />
                        </div>
                      </td>
                      <td className="px-4 py-6 text-center">
                        <div className="bg-blue-50 p-2 rounded-lg border border-blue-200">
                          <Input
                            type="number"
                            value={item?.quantity || ''}
                            onChange={(e) => updateLineItem(index, 'quantity', e?.target?.value)}
                            placeholder="1"
                            min="0"
                            step="0.001"
                            size="md"
                            className="text-center font-bold text-blue-900 bg-blue-50 w-full max-w-[100px]"
                          />
                        </div>
                      </td>
                      <td className="px-4 py-6 text-center">
                        <div className="bg-green-50 p-2 rounded-lg border border-green-200">
                          <Input
                            type="number"
                            value={item?.unit_price || ''}
                            onChange={(e) => updateLineItem(index, 'unit_price', e?.target?.value)}
                            placeholder="0.00"
                            min="0"
                            step="0.01"
                            size="md"
                            className="text-center font-bold text-green-900 bg-green-50 w-full max-w-[120px]"
                          />
                        </div>
                      </td>
                      <td className="px-4 py-6 text-center">
                        <div className="bg-purple-50 p-2 rounded-lg border border-purple-200">
                          <Input
                            type="number"
                            value={item?.tax_rate || ''}
                            onChange={(e) => updateLineItem(index, 'tax_rate', e?.target?.value)}
                            placeholder="0"
                            min="0"
                            max="100"
                            step="0.01"
                            size="md"
                            className="text-center font-bold text-purple-900 bg-purple-50 w-full max-w-[100px]"
                          />
                        </div>
                      </td>
                      <td className="px-4 py-6 text-right">
                        <div className="bg-gradient-to-r from-green-100 to-emerald-100 p-4 rounded-lg border-2 border-green-300 shadow-md">
                          <div className="text-lg font-bold text-green-800">
                            {formatCurrency(item?.line_total_with_tax)}
                          </div>
                          <div className="text-xs text-green-600 mt-1">
                            Tax: {formatCurrency(item?.tax_amount)}
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-6 text-center">
                        <button
                          type="button"
                          onClick={() => removeLineItem(index)}
                          className="text-red-600 hover:text-white hover:bg-red-600 p-3 rounded-full shadow-md hover:shadow-lg transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                          disabled={items?.length === 1}
                          title="Remove this item"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Enhanced Totals Section */}
          <div className="bg-gradient-to-r from-gray-50 via-blue-50 to-indigo-50 rounded-xl p-8 border-2 border-blue-200 shadow-lg">
            <div className="flex justify-end">
              <div className="w-full max-w-md bg-white rounded-xl p-8 shadow-xl border border-gray-200">
                <div className="flex items-center mb-6">
                  <div className="w-8 h-8 bg-green-600 rounded-lg flex items-center justify-center mr-3">
                    <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                    </svg>
                  </div>
                  <h4 className="text-xl font-bold text-gray-900">Invoice Summary</h4>
                </div>
                
                <div className="space-y-4">
                  <div className="flex justify-between py-3 border-b border-gray-200">
                    <span className="text-sm font-semibold text-gray-600">Subtotal:</span>
                    <span className="text-lg font-bold text-gray-900">{formatCurrency(totals?.subtotal)}</span>
                  </div>
                  <div className="flex justify-between py-3 border-b border-gray-200">
                    <span className="text-sm font-semibold text-gray-600">Tax Total:</span>
                    <span className="text-lg font-bold text-gray-900">{formatCurrency(totals?.totalTax)}</span>
                  </div>
                  <div className="bg-gradient-to-r from-green-50 to-emerald-50 p-4 rounded-lg border-2 border-green-300">
                    <div className="flex justify-between items-center">
                      <span className="text-lg font-bold text-green-800">Grand Total:</span>
                      <span className="text-2xl font-bold text-green-700">{formatCurrency(totals?.total)}</span>
                    </div>
                  </div>
                </div>
                
                {/* Items Count Badge */}
                <div className="mt-6 pt-4 border-t border-gray-200">
                  <div className="flex items-center justify-center">
                    <span className="inline-flex items-center px-4 py-2 bg-blue-100 text-blue-800 rounded-full text-sm font-semibold">
                      <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      {items?.length} Line Item{items?.length !== 1 ? 's' : ''}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {/* CRITICAL FIX: Enhanced Empty State - only show when truly empty and initialized */}
      {shouldShowEmptyState && (
        <div className="text-center py-16 bg-gradient-to-br from-gray-50 to-blue-50 border-2 border-dashed border-blue-300 rounded-xl">
          <div className="w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <svg className="w-10 h-10 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
            </svg>
          </div>
          <h3 className="text-2xl font-bold text-gray-900 mb-3">No Items Added Yet</h3>
          <p className="text-lg text-gray-600 mb-6 max-w-md mx-auto">
            Start building your invoice by adding products or services from your inventory.
          </p>
          <Button
            type="button"
            variant="primary"
            size="lg"
            onClick={handleAddItemClick}
            disabled={itemsLoading}
            className="px-8 py-4 text-lg font-semibold shadow-lg hover:shadow-xl transition-all duration-200"
          >
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
            </svg>
            Add Your First Item
          </Button>
        </div>
      )}

      {/* Enhanced Tax Configuration Info */}
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border-2 border-blue-200 rounded-xl p-6 shadow-md">
        <div className="flex items-start">
          <div className="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
            <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <div className="flex-1">
            <h5 className="text-lg font-bold text-blue-900 mb-2">Tax Configuration Settings</h5>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-3">
              <div className="bg-white p-3 rounded-lg border border-blue-200">
                <span className="text-xs font-semibold text-blue-700 uppercase tracking-wider">Tax Type</span>
                <div className="text-sm font-bold text-blue-900 mt-1">
                  {taxSettings?.taxType === 'tax_inclusive' ? 'Tax Inclusive' : 'Tax Exclusive'}
                </div>
              </div>
              <div className="bg-white p-3 rounded-lg border border-blue-200">
                <span className="text-xs font-semibold text-blue-700 uppercase tracking-wider">Tax Level</span>
                <div className="text-sm font-bold text-blue-900 mt-1">
                  {taxSettings?.taxLevel === 'line_item_level' ? 'Line Item Level' : 'Transaction Level'}
                </div>
              </div>
            </div>
            <p className="text-sm text-blue-800 leading-relaxed">
              <strong>Tax Calculation:</strong> Prices are calculated based on your organization's tax configuration. 
              {taxSettings?.taxType === 'tax_inclusive' ? ' Tax amounts are included in the unit prices you enter.' : ' Tax amounts are added on top of the unit prices you enter.'}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LineItemsTable;